import { mapGetters } from 'vuex';
import { closeCurrentTag } from '@/utils/tags';
import { handleDatabaseRequestError } from '@/utils/error';
import { array_concat, asyncForEach, deepClone, objectsAreEqual } from '@/utils';
import { entityToStore } from '@/utils/store';

const debounce = require('lodash.debounce');

export default {
  props: {
    autoGeneratedId: {
      default: true,
      type: Boolean
    },
    definition: {
      default() {
        return {};
      },
      type: Object
    },
    disabled: {
      default: false,
      type: Boolean
    },
    id: {
      type: [Number, Object],
      default: 0
    },
    fetchById: {
      default: id => {
        throw new Error('fetchById is not provided as a prop');
      },
      type: Function
    },
    formHasChanged: {
      type: Function
    },
    deleteById: {
      type: Function
    },
    preCreateFetch: {
      default: () => Promise.resolve(),
      type: Function
    },
    isEdit: {
      default: false,
      type: Boolean
    },
    fetchOnCreate: {
      default: false,
      type: Boolean
    },
    itemType: {
      default: '',
      type: String
    },
    returnTo: {
      default: '',
      type: String
    },
    save: {
      default: data => {
        throw new Error('save is not provided as a prop');
      },
      type: Function
    },
    pageLoading: {
      type: Boolean,
      default: false
    },
    isEmbedded: {
      type: Boolean,
      default: false
    },
    showDeleteButton: {
      type: Boolean,
      default: true
    },
    showSaveButton: {
      type: Boolean,
      default: true
    },
    showSaveAndCloseButton: {
      type: Boolean,
      default: true
    },
    showSaveAndNextButton: {
      type: Boolean,
      default: true
    },
    forceValues: {
      type: Object,
      default: () => ({})
    },
    saveButtonPosition: {
      type: String,
      default: 'top'
    },
    preventDefault: {
      type: Boolean,
      default: false
    },
    getDependencies: {
      type: Function,
      default: () => []
    },
    promptRouteLeave: Function,
    injectFormValues: {
      type: Object, // When object is changed, the values of injectFormValues are copied into form, changing the fields' values
      default: () => ({})
    },
    getTagTitle: Function,
    getLayout: Function,
    getRouteOnSave: Function
  },
  data() {
    const fieldsDefinition = this.definition;
    const fieldNames = Object.keys(fieldsDefinition);
    this.defaultForm = Object.assign(
      {},
      fieldNames.reduce((form, name) => {
        form[name] = fieldsDefinition[name].defaultValue;
        return form;
      }, {})
    );

    const fields = {};
    fieldNames.reduce((target, name) => {
      const field = fieldsDefinition[name];
      if (field.type) {
        target[name] = field;
      }
      return target;
    }, fields);

    return {
      errorMessage: '',
      formItemStyle: {},
      errors: {},
      formData: {},
      form: Object.assign({}, this.defaultForm),
      actionLoading: false,
      tempRoute: {},
      userListOptions: [],
      formLabelPosition: 'right',
      forceExit: false,
      initvals: {},
      preSaveHooks: {},
      postSaveHooks: {},
      requestErrorHooks: {},
      editorNumber: 0,
      initialFocusState: {},
      fetchError: null
    };
  },
  computed: {
    innerDisabled() {
      return this.disabled || !!this.fetchError;
    },
    definitionsObject() {
      return this.definition;
    },
    fields() {
      const fieldsDefinition = this.definition;
      const fieldNames = Object.keys(fieldsDefinition);

      const fields = {};
      fieldNames.reduce((target, name) => {
        const field = fieldsDefinition[name];
        if (field.type) {
          target[name] = field;
        }
        return target;
      }, fields);

      return fields;
    },
    columnsFields() {
      if (this.getLayout) {
        const layout = this.getLayout(this);
        const columnsProps = this.generateColumnsPropertiesFromPageLayout(layout);
        return columnsProps.columnFields;
      }
      return this.splitFieldsInHalfAsArray(this.fields);
    },
    columnsWidthPercentages() {
      if (this.getLayout) {
        const layout = this.getLayout(this);
        const columnsProps = this.generateColumnsPropertiesFromPageLayout(layout);
        return columnsProps.widths;
      }
      return [];
    },
    ...mapGetters(['editorOptions', 'validationErrorInputPrefix']),
    showSave() {
      return !this.innerDisabled && this.showSaveButton;
    },
    showSaveAndClose() {
      return !this.innerDisabled && this.showSaveAndCloseButton;
    },
    showSaveAndNext() {
      return !this.innerDisabled && this.showSaveAndNextButton && this.editorOptions.btnSaveAndNext;
    },
    showDelete() {
      return !this.innerDisabled && this.isEdit && this.showDeleteButton && this.editorOptions.btnDelete;
    },
    fetchErrorCode() {
      return (this.fetchError && this.fetchError.response && this.fetchError.response.status) || null;
    },
    fetchErrorMessage() {
      return typeof this.fetchError === 'string' ? this.fetchError : null;
    },
    pageMessage() {
      if (this.fetchErrorMessage) {
        return this.fetchErrorMessage;
      }
      if (this.fetchErrorCode) {
        return this.$i18n.t('editor.entityFetchError');
      }
      if (this.innerDisabled) {
        return this.$i18n.t('permission.pageIsReadonly');
      }
      return '';
    }
  },
  watch: {
    forceValues(val, oldVal) {
      if (objectsAreEqual(val, oldVal)) {
        this.refreshFormValues();
      }
    },
    definition(definition, oldVal) {
      if (JSON.stringify(definition) !== JSON.stringify(oldVal)) {
        this.refreshFormValues();
      }
    },
    id(val) {
      if (this.isEdit) {
        const id = this.id || (this.$route.params && this.$route.params.id);
        this.fetchData(id);
      }
    },
    form: {
      deep: true,
      handler: function(val) {
        this.$store.dispatch('editor/update', { form: val, editorNumber: this.editorNumber });
        const flatFields = this.getFlatFields();
        const changedKeys = Object.keys(val).filter(key => val[key] !== (this.formSnapshot && this.formSnapshot[key]));
        Object.keys(flatFields).forEach(name => {
          if (flatFields[name].valueOnFormChange) {
            const promiseOrNewVal = flatFields[name].valueOnFormChange(val, this.form[name], changedKeys);
            const that = this;
            Promise.resolve(promiseOrNewVal).then(function(newVal) {
              const oldVal = that.form[name];
              let valueIsChanged = false;
              if (Array.isArray(newVal) && typeof oldVal === 'object') {
                valueIsChanged = that.formObjectsAreDifferent(newVal, Object.values(oldVal));
              } else {
                valueIsChanged = newVal !== oldVal;
              }
              if (valueIsChanged) {
                that.$set(that.form, name, newVal);
              }
            });
          }
        });
        this.formSnapshot = Object.assign({}, val);

        this.$emit('form', val);
        this.debouncedValidate();
      }
    },
    injectFormValues(injectFormValues) {
      const newValues = Object.keys(injectFormValues).reduce((newValues, key) => {
        newValues[key] = injectFormValues[key];
        this.$set(this.form, key, injectFormValues[key]);
        return newValues;
      }, {});
      this.replaceForm(Object.assign({}, this.form, newValues));
    }
  },
  async created() {
    // Why need to make a copy of this.$route here?
    // Because if you enter this page and quickly switch tag, may be in the execution of the setTagsViewTitle function,
    // this.$route is no longer pointing to the current page
    // https://github.com/PanJiaChen/vue-element-admin/issues/1221
    this.tempRoute = Object.assign({}, this.$route);

    this.editorNumber = await this.$store.dispatch('editor/register');
    window.addEventListener('beforeunload', this.beforeUnload, false);
    if (this.isEdit || this.fetchOnCreate) {
      const id = this.id || (this.$route.params && this.$route.params.id);
      this.fetchData(id).then(data => {
        const dependencies = this.getDependencies(data);
        dependencies.forEach(dep => {
          const params = dep.params || {};
          if (dep.inactiveID) {
            params.query = {
              includeID: dep.inactiveID
            };
          }
          this.$store.dispatch(dep.entity + '/getItems', params);
        });
      });
    } else {
      this.fetchDependencies();
      await this.handleCrucialFetch(this.preCreateFetch());
      this.handleInitialFocus('fetched');
    }
    this.refreshFormValues();
    this.$store.dispatch('notify/disableErrorCodePrefix', this.validationErrorInputPrefix);
  },
  destroyed() {
    window.removeEventListener('beforeunload', this.beforeUnload);
    window.removeEventListener('resize', this.handleResize);
    this.$store.dispatch('notify/enableErrorCodePrefix', this.validationErrorInputPrefix);
    this.$store.dispatch('editor/unregister', this.editorNumber);
  },
  mounted() {
    this.debouncedValidate = debounce(this.validateForm, 200);
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    this.handleInitialFocus('mounted');
  },
  methods: {
    replaceForm(newForm) {
      if (JSON.stringify(this.form) !== JSON.stringify(newForm)) {
        this.form = Object.assign({}, newForm);
      }
    },
    handleInitialFocus(action) {
      this.initialFocusState[action] = true;
      const { mounted, fetched } = this.initialFocusState;
      if (mounted && fetched) {
        this.setFocus();
      }
    },
    validateForm() {
      const form = this.$refs.form;
      if (form) {
        form.validate().catch(_ => {});
      }
    },
    getRules(fields) {
      return Object.keys(fields).reduce((rules, key) => {
        const staticRules = fields[key].rules;
        const dynamicRules = fields[key].dynamicRules && fields[key].dynamicRules(this.form);
        rules[key] = array_concat(staticRules || [], dynamicRules || []);
        return rules;
      }, {});
    },
    getWidthFromPercentage(percentage, columns) {
      if (percentage === undefined) {
        return 24 / (this.isEmbedded ? 1 : columns.length);
      }
      return Math.floor(0.01 * percentage * 24);
    },
    generateColumnsPropertiesFromPageLayout(layout, tabIndex) {
      const { named, sections } = layout;
      const columns = [];
      const columnWidths = [];
      const flatFields = this.getFlatFields();
      if (named) {
        sections.forEach(({ fields, width, containers }) => {
          const columnFields = fields.reduce((column, fieldName) => {
            column[fieldName] = flatFields[fieldName];
            return column;
          }, {});

          columns.push(columnFields);
          columnWidths.push(width);
        });
      } else {
        const fieldsOnThisPage = tabIndex === undefined ? flatFields : this.fields[tabIndex];
        const fieldNamesStack = Object.keys(fieldsOnThisPage);
        sections.forEach(({ capacity, width }) => {
          if (!fieldNamesStack.length) {
            return;
          }
          let columnFields = {};
          if (capacity === undefined) {
            columnFields = fieldNamesStack.reduce((column, fieldName) => {
              column[fieldName] = flatFields[fieldName];
              return column;
            }, {});
          } else {
            for (let i = 0; i < capacity && fieldNamesStack.length; i++) {
              const fieldName = fieldNamesStack.shift();
              columnFields[fieldName] = flatFields[fieldName];
            }
          }
          columns.push(columnFields);
          columnWidths.push(width);
        });
      }
      return { columnFields: columns, widths: columnWidths };
    },
    getLabel(spec, name) {
      const specs = this.getProps(spec);
      return (specs.caption && this.$i18n.t(`${specs.caption}`)) || this.$i18n.t(`common.${name}`);
    },
    getPanels(tabIndex) {
      if (this.getLayout) {
        const layout = this.getLayout(this);
        return layout.groups[tabIndex].panels !== undefined ? layout.groups[tabIndex].panels : null;
      }
    },
    getPanel(fieldName, tabIndex) {
      const panels = this.getPanels(tabIndex);
      if (!panels) {
        return false;
      }
      return panels.find(panel => panel.fields.some(fn => fn === fieldName));
    },
    isFirstOfPanel(fieldName, tabIndex) {
      const panel = this.getPanel(fieldName, tabIndex);
      return panel && panel.fields.indexOf(fieldName) === 0;
    },
    isLastOfPanel(fieldName, tabIndex) {
      const panel = this.getPanel(fieldName, tabIndex);
      return panel && panel.fields.indexOf(fieldName) === panel.fields.length - 1;
    },
    getPanelCaption(fieldName, tabIndex) {
      const panel = this.getPanel(fieldName, tabIndex);
      return panel && panel.caption;
    },
    getVisibleProp(spec) {
      const staticProp = this.getStaticProps(spec).visible;
      if (staticProp === false) {
        return staticProp;
      }
      return staticProp || this.getDynamicProps(spec).visible;
    },
    getDisabledProp(spec) {
      return this.innerDisabled || !!this.getStaticProps(spec).disabled || this.getDynamicProps(spec).disabled;
    },
    getProps(spec) {
      return { ...this.getStaticProps(spec), ...this.getDynamicProps(spec) };
    },
    getStaticProps(spec) {
      return spec.props || {};
    },
    getDynamicProps(spec) {
      return (spec.dynamicProps && spec.dynamicProps(this.form)) || {};
    },
    setFocus() {
      this.$nextTick(() => {
        const firstComponent = this.getFirstFocusableInputField();
        if (firstComponent && typeof firstComponent.focus === 'function') {
          firstComponent.focus();
        }
      });
    },
    createRef(grandparent, parent, child) {
      let me = 'inputField' + grandparent.toString().padStart(4, '0');
      if (parent !== undefined) {
        me += parent.toString().padStart(4, '0');
      }
      if (child !== undefined) {
        me += child.toString().padStart(4, '0');
      }
      return me;
    },
    getFirstFocusableInputField() {
      const inputFieldRefKeys = Object.keys(this.$refs)
        .filter(key => key.startsWith('inputField'))
        .sort();
      let key;
      while ((key = inputFieldRefKeys.shift())) {
        const component = this.$refs[key][0];
        if (component && !component.disabled && !component.$attrs.disabled) {
          return component;
        }
      }
      return undefined;
    },
    handleCrucialFetch(promise) {
      this.fetchError = null;
      const pageVersionBeforeFetch = this.$store.getters['pageVersion'];
      return promise.catch(error => {
        console.log('crucial fetch error', error);
        const pageVersion = this.$store.getters['pageVersion'];
        if (pageVersion !== pageVersionBeforeFetch) {
          return;
        }
        if (error && error.response) {
          this.fetchError = error;
        }
      });
    },
    getFlatFields() {
      let flatFields = this.fields;

      if (Array.isArray(flatFields)) {
        flatFields = flatFields.reduce((acc, tabFields) => {
          acc = { ...acc, ...tabFields };
          return acc;
        }, {});
      }
      return flatFields;
    },
    defaultFormHasChanged() {
      const userEditableForm = {};
      const userEditableFormData = {};

      const flatFields = this.getFlatFields();

      Object.keys(flatFields).forEach(key => {
        userEditableForm[key] = this.form[key];
        userEditableFormData[key] = this.formData[key];
      });
      // Debug code
      const stringifiedForm = JSON.stringify(userEditableForm);
      const stringifiedFormData = JSON.stringify(userEditableFormData);
      if (stringifiedForm !== stringifiedFormData) {
        console.log(stringifiedFormData);
        console.log(stringifiedForm);
      }
      // End debug code
      return JSON.stringify(userEditableForm) !== JSON.stringify(userEditableFormData);
    },
    clearInputErrorFor(name, additionalFields) {
      delete this.errors[name.toLowerCase()];
      if (Array.isArray(additionalFields)) {
        additionalFields.forEach(field => {
          delete this.errors[field.toLowerCase()];
        });
      }
    },
    getInputErrors(errors, name, spec, form) {
      const { showErrorsFromFields, warnings } = spec;
      const warningMessages = this.getInputWarnings(name, warnings, form);
      const fieldError = errors[name.toLowerCase()];
      const errorsFromOtherFields = this.getErrorsFromFields(errors, showErrorsFromFields);
      return array_concat(fieldError, errorsFromOtherFields, warningMessages);
    },
    getInputWarnings(name, warningsSpec, form) {
      const warningMessages = [];
      if (Array.isArray(warningsSpec)) {
        const warningCallback = error => {
          if (error) {
            warningMessages.push({ message: error.message, type: 'warning' });
          }
        };
        warningsSpec.forEach(warning => {
          warning.validator(null, form[name], warningCallback);
        });
      }
      return warningMessages;
    },
    getErrorsFromFields(errors, fields) {
      if (!Array.isArray(fields)) {
        return [];
      }
      return fields
        .map(field => errors[field.toLowerCase()])
        .filter(error => error)
        .reduce((flat, err) => [...flat, ...err], []);
    },
    doPromptRouteLeave() {
      if (this.forceRouteLeave) {
        return Promise.resolve();
      }
      if (this.promptRouteLeave) {
        return this.promptRouteLeave();
      }
      return this.$store.dispatch('notify/confirm', {
        title: this.$i18n.t('common.warning'),
        message: this.$i18n.t('common.leave_unsaved_warning'),
        confirmButtonText: this.$i18n.t('common.save'),
        cancelButtonText: this.$i18n.t('common.dontSave')
      });
    },
    handleRouteLeave(to, from, next) {
      const hasChanged = this.formHasChanged ? this.formHasChanged() : this.defaultFormHasChanged();
      if (hasChanged && !this.forceExit) {
        const saveErrorMsg = this.$i18n.t('error.save');
        next(false);
        this.doPromptRouteLeave()
          .then(async () => {
            try {
              const done = await this.persistForm();
              if (done) {
                next();
              }
            } catch (err) {
              this.$store.dispatch('toast', { message: saveErrorMsg });
              console.log(err);
            }
          })
          .catch(action => {
            if (action === 'cancel') {
              next();
            } else {
              next(false);
            }
          });
      } else {
        next();
      }
    },
    beforeUnload(e) {
      const hasChanged = this.formHasChanged ? this.formHasChanged() : this.defaultFormHasChanged();
      if (hasChanged) {
        // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    },
    fetchDependencies(dep) {
      const dependencies = this.getDependencies();
      dependencies.forEach(dep => {
        const params = dep.params || {};
        this.$store.dispatch(dep.entity + '/getItems', params);
      });
    },
    handleResize() {
      const { width } = this.$el.getBoundingClientRect();
      if (width <= 767) {
        this.formLabelPosition = 'top';
      } else {
        this.formLabelPosition = 'right';
      }
    },
    splitFieldsInHalfAsArray(fields) {
      const keys = Object.keys(fields);
      if (keys.length < 4) {
        const columns = [];
        columns.push(fields);
        return columns;
      }
      let columns = [];
      columns = keys.reduce((acc, key, index) => {
        const columnIndex = index < keys.length / 2 ? 0 : 1;
        if (!acc[columnIndex]) {
          acc[columnIndex] = {};
        }
        acc[columnIndex][key] = fields[key];
        return acc;
      }, columns);
      return columns;
    },
    handleInit(name, init) {
      this.formData[name] = init;
    },
    clearForm() {
      this.formData = {};
      this.refreshFormValues();
      this.$refs.form.clearValidate();
    },
    refreshFormValues() {
      // this.form cannot be Computed because it is passed as v-model for input components
      const fieldsDefinition = this.definitionsObject;
      const fieldNames = Object.keys(fieldsDefinition);
      const defaultForm = Object.assign(
        {},
        fieldNames.reduce((form, name) => {
          form[name] = fieldsDefinition[name].defaultValue;
          return form;
        }, {})
      );
      this.replaceForm(Object.assign({}, defaultForm, this.formData, this.forceValues));
      this.formData = deepClone(this.form);
    },
    fetchData(id) {
      this.$store.dispatch('notify/disableErrorHTTPCode', 403);
      this.$store.dispatch('notify/disableErrorHTTPCode', 404);
      return this.handleCrucialFetch(
        this.fetchById(id)
          .then(data => {
            this.formData = Object.assign(this.formData, data);
            this.initvals = Object.assign({}, this.formData, data);
            this.$set(this.formData, 'id', data.id);
            this.refreshFormValues();
            this.refreshTagTitle(data);
            this.$refs.form.clearValidate();
            this.handleInitialFocus('fetched');
            this.$emit('fetched');
            return data;
          })
          .finally(_ => {
            this.$store.dispatch('notify/enableErrorHTTPCode', 403);
            this.$store.dispatch('notify/enableErrorHTTPCode', 404);
          })
      );
    },
    refreshTagTitle(item) {
      if (this.isEdit) {
        const newTag = this.getTagTitle ? { title: this.getTagTitle(item) } : { name: item.name || item.code || '' };
        this.setTagsViewTitle(newTag);
      }
    },
    mapToPath(statusCode) {
      switch (statusCode) {
        case 403:
          return '/403';
        case 404:
          return '/404';
        default:
          return null;
      }
    },
    registerHook(type, name, hook) {
      if (hook) {
        if (!this[type][name]) {
          this[type][name] = [];
        }
        this[type][name].push(hook);
      } else {
        delete this[type][name];
      }
    },
    registerRequestErrorHook(name, hookDefinition) {
      this.registerHook('requestErrorHooks', hookDefinition.code, hookDefinition.action);
    },
    registerPreSaveHook(name, hook) {
      this.registerHook('preSaveHooks', name, hook);
    },
    registerPostSaveHook(name, hook) {
      this.registerHook('postSaveHooks', name, hook);
    },
    async performPreSaveHooks() {
      const componentHooks = Object.keys(this.preSaveHooks).map(name => this.preSaveHooks[name]);

      await asyncForEach(componentHooks, async hooks => {
        await asyncForEach(hooks, async hook => {
          await hook(this.form);
        });
      });
    },
    async performPostSaveHooks() {
      const componentHooks = Object.keys(this.postSaveHooks).map(name => this.postSaveHooks[name]);

      await asyncForEach(componentHooks, async hooks => {
        await asyncForEach(hooks, async hook => {
          await hook(this.form);
        });
      });
    },
    setTagsViewTitle({ name, title }) {
      if (this.isEmbedded) {
        return;
      }
      if (title === undefined) {
        title = this.$i18n.t('route.edit' + this.itemType) + ' ' + (name || '');
      }
      const route = Object.assign({}, this.tempRoute, {
        title: `${title}`
      });
      this.$store.dispatch('updateVisitedView', route);
    },
    async persistForm() {
      this.errorMessage = '';
      this.replaceForm(Object.assign(this.form, this.forceValues));
      try {
        await this.$refs.form.validate();
      } catch (err) {
        this.$store.dispatch('notify/formValidationFailed');
        return false;
      }

      try {
        await this.performPreSaveHooks();
      } catch (err) {
        return false;
      }
      if (!this.isEdit && this.autoGeneratedId) {
        // make sure no id is present
        delete this.form.id;
      }
      this.actionLoading = true;
      this.$emit('save');
      this.errors = Object.assign({});
      const _form = Object.assign({}, this.form);
      try {
        const response = await this.save(this.form);
        // This fixes bug that form magically resets after save()
        this.replaceForm(_form);
        if (!this.preventDefault && (!response || !(typeof response === 'string'))) {
          if (this.isEdit) {
            this.$store.dispatch('notify/dataUpdated');
          } else {
            this.$store.dispatch('notify/dataCreated');
          }
        }
        this.actionLoading = false;
        this.formData = Object.assign(this.formData, this.form, {});
        this.refreshTagTitle(this.form);
        this.$emit('saved', this.form);
        this.$emit('persisted', response);
        this.$root.$emit('editor-saved', this.editorNumber, this.form);

        try {
          await this.performPostSaveHooks();
        } catch (err) {
          return false;
        }
        return response || true;
      } catch (error) {
        // This fixes bug that form magically resets after save()
        this.replaceForm(_form);
        this.errors = handleDatabaseRequestError(this, error, this.requestErrorHooks, this.preventDefault);
        this.actionLoading = false;
        return false;
      }
    },
    async handleSave() {
      const item = await this.persistForm();
      if (item && !this.isEdit && !this.isEmbedded && !this.preventDefault) {
        closeCurrentTag(
          this,
          this.getRouteOnSave
            ? this.getRouteOnSave(item)
            : {
                name: 'Edit' + this.itemType,
                params: { id: item.id }
              },
          false
        );
      }
      return item;
    },
    async handleDelete() {
      if (!this.isEdit) {
        throw new Error('Delete not allowed');
      }
      const id = this.id || (this.$route.params && this.$route.params.id);
      try {
        await this.$store.dispatch('notify/deleteConfirm');
        this.actionLoading = true;
        this.deleteById ? await this.deleteById(id) : await this.defaultDeleteById(id);
        if (!this.preventDefault) {
          this.forceExit = true;
          closeCurrentTag(this, false, { name: this.returnTo });
        }
      } catch (_) {
        // do nothing
      }
      this.actionLoading = false;
    },
    defaultDeleteById(id) {
      const entityStoreName = entityToStore(this.itemType);
      return this.$store.dispatch(entityStoreName + '/deleteItem', { id }).then(() => {
        this.$store.dispatch('notify/deleteCompleted');
      });
    },
    async saveAndClose() {
      const done = await this.persistForm();
      if (done && !this.preventDefault) {
        if (this.getRouteOnSave) {
          closeCurrentTag(this, this.getRouteOnSave(), false);
        } else {
          closeCurrentTag(this, false, { name: this.returnTo });
        }
      }
    },
    async saveAndNext() {
      const done = await this.persistForm();
      if (done && !this.preventDefault) {
        const { params } = this.$route;
        const name = 'Create' + this.itemType;

        if (this.isEdit) {
          this.$router.push({ name, params });
        } else {
          let href = this.$router.resolve({ name, params }).href;
          href = href.replace(/^#/g, '');
          this.$router.push({ path: '/redirect' + href });
        }
      }
    },
    formArraysAreDifferent(arr1, arr2) {
      if (arr1.length !== arr2.length) {
        return true;
      }
      const _arr1 = arr1.sort();
      const _arr2 = arr2.sort();
      const isDifferent = _arr1.some((val, index) => {
        if (_arr1[index] && typeof _arr2[index] === typeof {}) {
          return this.formObjectsAreDifferent(_arr1[index], _arr2[index]);
        }
        return _arr1[index] !== _arr2[index];
      });
      return isDifferent;
    },
    formObjectsAreDifferent(obj1, obj2) {
      const isDifferent = Object.keys(obj1).some(key => {
        if (obj1[key] && typeof obj1[key] === typeof {}) {
          if (Array.isArray(obj1[key])) {
            return this.formArraysAreDifferent(obj1[key], obj2[key]);
          } else if (obj2 && obj2[key] !== undefined) {
            return this.formObjectsAreDifferent(obj1[key], obj2[key]);
          }
          return false;
        }
        // Assume null to be same as empty string
        const _val1 = obj1[key] === null ? '' : obj1[key];
        const _val2 = obj2 === null || obj2[key] === null ? '' : obj2[key];
        // Debug code
        // if (_val1 !== _val2) {
        //   console.log('different: ', { key, val1: obj1[key], val2: obj2[key] });
        // }
        return _val1 !== _val2;
      });
      return isDifferent;
    },
    async close() {
      try {
        const hasChanged = this.formHasChanged ? this.formHasChanged() : this.defaultFormHasChanged();
        if (hasChanged) {
          await this.$store.dispatch('notify/closeUnsavedConfirm');
        }
        this.forceExit = true;
        closeCurrentTag(this, false, { name: this.returnTo });
      } catch (err) {
        // do nothing and stay on page
      }
    }
  }
};
